import { useCallback, useEffect, useState, useRef } from 'react';
import BigButton from '../../button/BigButton';
import { toast } from 'react-toastify';
import { UpdateProductRequest, useProductStore, categories } from '../../../store/useProduct';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../../form/Input';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import { formatNumberWithComma, getDiscountRate } from '../../../util/util';
import TemplateImageModal from "./modal/TemplateImageModal";
import TemplateFontModal from "./modal/TemplateFontModal";
import DefaultImageModal from "../../modal/DefaultImageModal";
import TemplateStyleModal from "./modal/TemplateStyleModal";

/**
 * @author assistant
 * @date 2025.01.04
 * @description 상품 수정 페이지
 */
const ProductUpdate = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { updateProduct, getProductDetail, productDetail, getProductPreview, searchCategories } = useProductStore();
  
  // URL에서 직접 ID 추출
  const urlParts = window.location.pathname.split('/');
  const productId = urlParts[urlParts.length - 1];

  // 폼 데이터
  const [productCode, setProductCode] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [discountPrice, setDiscountPrice] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [custInfoCount, setCustInfoCount] = useState<string>('1');
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const lastPreviewParamsRef = useRef<string>('');
  const previewRequestInFlightRef = useRef<string | null>(null);
  const previewCacheRef = useRef<Map<string, string>>(new Map());
  const scheduledPreviewRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRequestedInitialPreviewRef = useRef(false);
  const hasInitializedPreviewRef = useRef(false);
  const [categoryName, setCategoryName] = useState<string>('');
  const [categoryList, setCategoryList] = useState<categories[]>([]);
  const [showCategoryList, setShowCategoryList] = useState<boolean>(false);
  const categoryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 이미지 관련
  const [newThumbnail, setNewThumbnail] = useState<{file: File; id: string} | null>(null);
  const [newInline, setNewInline] = useState<{file: File; id: string} | null>(null);
  const [newDetailImages, setNewDetailImages] = useState<{file: File; id: string}[]>([]);
  const [existingThumbnail, setExistingThumbnail] = useState<string>('');
  const [existingInline, setExistingInline] = useState<string>('');
  const [newEndImage, setNewEndImage] = useState<{file: File; id: string} | null>(null);
  const [existingEnd, setExistingEnd] = useState<string>('');
  const [existingDetailImages, setExistingDetailImages] = useState<string[]>([]);
  const [newSampleFile, setNewSampleFile] = useState<{file: File; id: string} | null>(null);
  const [existingSampleFile, setExistingSampleFile] = useState<string>('');
  const [sampleFiles, setSampleFiles] = useState<File[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string>('');

  // iframe 스크롤 위치 저장용
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });


  // 템플릿 이미지 모달 상태
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateFontModalOpen, setTemplateFontModalOpen] = useState(false);
  const [defaultImageModalOpen, setDefaultImageModalOpen] = useState(false);
  const [templateStyleModalOpen, setTemplateStyleModalOpen] = useState(false);
  const closeTemplateStyleModal = async () => {
    await updatePreview({ force: true });
    setTemplateStyleModalOpen(false);
  };

  // 상품 정보 로드
  useEffect(() => {
    if (productId) {
      getProductDetail(productId);
    }
  }, [productId, getProductDetail]);

  // 상품 정보가 로드되면 폼에 데이터 설정
  useEffect(() => {
    if (productDetail) {
      setProductCode(productDetail.productCode);
      setProductName(productDetail.productName);
      setPrice(formatNumberWithComma(productDetail.price));
      setCustInfoCount(productDetail.custInfoCount.toString());
      setDescription(productDetail.description);

      if (productDetail.discountPrice) {
        setDiscountPrice(formatNumberWithComma(productDetail.discountPrice));
      }

      if (productDetail.thumbnailFile) {
        setExistingThumbnail(productDetail.thumbnailFile.fileFullUrl);
      }

      if (productDetail.inlineFile) {
        setExistingInline(productDetail.inlineFile.fileFullUrl);
      }

      if (productDetail.detailFiles && productDetail.detailFiles.length > 0) {
        setExistingDetailImages(productDetail.detailFiles.map(file => file.fileFullUrl));
      }

      if(productDetail.endFile) {
        setExistingEnd(productDetail.endFile.fileFullUrl);
      }

      // 카테고리 이름 설정
      if (productDetail.categoryName) {
        setCategoryName(productDetail.categoryName);
      }

      // 샘플 파일 설정
      if (productDetail.sampleFile) {
        setExistingSampleFile(productDetail.sampleFile.fileFullUrl);
      }
    }
  }, [productDetail]);

  const productThumbnailId = productDetail?.thumbnailFile?.id;
  const productInlineId = productDetail?.inlineFile?.id;
  const productEndId = productDetail?.endFile?.id;
  const newThumbnailId = newThumbnail?.id;
  const newInlineId = newInline?.id;
  const newEndImageId = newEndImage?.id;

  // 이미지 변경 시 미리보기 업데이트
  const updatePreview = useCallback(async ({ force = false }: { force?: boolean } = {}) => {
    if (!productId) return;

    const thumbnailId = newThumbnailId || productThumbnailId;
    const inlineId = newInlineId || productInlineId;
    const endId = newEndImageId || productEndId;

    if (!thumbnailId || !inlineId) {
      return;
    }

    const requestKey = JSON.stringify({
      productId,
      thumbnailId,
      inlineId,
      endId: endId || null,
    });

    if (!force) { //불필요한 재호출 방지용
      if (lastPreviewParamsRef.current === requestKey) {
        const cached = previewCacheRef.current.get(requestKey);
        if (cached) {
          setPreviewHtml(cached);
        }
        return;
      }

      const cached = previewCacheRef.current.get(requestKey);
      if (cached) {
        setPreviewHtml(cached);
        lastPreviewParamsRef.current = requestKey;
        return;
      }

      if (previewRequestInFlightRef.current === requestKey) {
        return;
      }
    } else {
      previewCacheRef.current.delete(requestKey);
    }

    previewRequestInFlightRef.current = requestKey;

    try {
      const preview = await getProductPreview(productId, thumbnailId, inlineId, endId);
      setPreviewHtml(preview);
      previewCacheRef.current.set(requestKey, preview);
      lastPreviewParamsRef.current = requestKey;
    } catch {
      lastPreviewParamsRef.current = '';
      previewCacheRef.current.delete(requestKey);
    } finally {
      if (previewRequestInFlightRef.current === requestKey) {
        previewRequestInFlightRef.current = null;
      }
    }
  }, [productId, newThumbnailId, newInlineId, newEndImageId, productThumbnailId, productInlineId, productEndId, getProductPreview]);

  useEffect(() => {
    if (scheduledPreviewRef.current) {
      clearTimeout(scheduledPreviewRef.current);
    }

    scheduledPreviewRef.current = setTimeout(() => {
      scheduledPreviewRef.current = null;
      updatePreview();
    }, 200);

    return () => {
      if (scheduledPreviewRef.current) {
        clearTimeout(scheduledPreviewRef.current);
        scheduledPreviewRef.current = null;
      }
    };
  }, [updatePreview]);

  useEffect(() => {
    lastPreviewParamsRef.current = '';
    previewCacheRef.current.clear();

    hasRequestedInitialPreviewRef.current = false;

    if (hasInitializedPreviewRef.current) {
      if (scheduledPreviewRef.current) {
        clearTimeout(scheduledPreviewRef.current);
        scheduledPreviewRef.current = null;
      }
    } else {
      hasInitializedPreviewRef.current = true;
    }
  }, [productId]);

  useEffect(() => {
    if (
      !hasRequestedInitialPreviewRef.current &&
      productThumbnailId &&
      productInlineId
    ) {
      hasRequestedInitialPreviewRef.current = true;
      updatePreview();
    }
  }, [productThumbnailId, productInlineId, productEndId, updatePreview]);

  // 특정 컨텐츠(wonguk_img_container)의 bottom row가 페이지 여백을 넘으면 통째로 숨기고, 잘림 표시("…") 추가
  const enforceWongukBounds = useCallback((iframe: HTMLIFrameElement) => {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const pageNodes = Array.from(doc.querySelectorAll('article')) as HTMLElement[];
    if (!pageNodes.length) return;

    pageNodes.forEach((page) => {
      const style = doc.defaultView?.getComputedStyle(page);
      const padTop = style ? parseFloat(style.paddingTop || '0') : 0;
      const padBottom = style ? parseFloat(style.paddingBottom || '0') : 0;
      const pageRect = page.getBoundingClientRect();
      const contentTop = pageRect.top + padTop;
      const contentBottom = pageRect.bottom - padBottom;

      const containers = Array.from(page.querySelectorAll('.wonguk_img_container')) as HTMLElement[];
      containers.forEach((container) => {
        const bottomRow = container.querySelector('.row.bottom') as HTMLElement | null;
        if (!bottomRow) return;

        const rowRect = bottomRow.getBoundingClientRect();
        // bottomRow 전체가 내용 영역에 들어가지 않으면 완전히 제거
        const fits = rowRect.top >= contentTop && rowRect.bottom <= contentBottom;
        const indicator = container.querySelector('.wonguk_cut_indicator') as HTMLElement | null;
        if (!fits) {
          if (bottomRow.style.display !== 'none') bottomRow.style.display = 'none';
          if (!indicator) {
            const ind = doc.createElement('div');
            ind.className = 'wonguk_cut_indicator';
            ind.textContent = '⚠️ 하단 행이 페이지 여백을 넘어 생략되었습니다. 실제 결과물에선 다음 페이지로 넘어갑니다';
            ind.setAttribute(
              'style',
              [
                'margin-top:10px',
                'width:100%',
                'text-align:center',
                'font-size:13px',
                'font-weight:700',
                'line-height:1.3',
                'color:#7c2d12',            /* amber-900 */
                'background:rgba(254,243,199,0.95)', /* amber-100 */
                'border:1px dashed #f59e0b', /* amber-500 */
                'border-radius:6px',
                'padding:8px 10px',
                'box-shadow:0 2px 6px rgba(0,0,0,0.08)'
              ].join(';')
            );
            container.appendChild(ind);
          }
        } else {
          // 들어가면 원복
          if (bottomRow.style.display === 'none') bottomRow.style.display = '';
          if (indicator) indicator.remove();
        }
      });
    });
  }, []);

  // 자수 이미지 컨테이너의 하단 이미지가 페이지 여백을 넘으면 숨기고, 잘림 표시 추가
  const enforceJasuBounds = useCallback((iframe: HTMLIFrameElement) => {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;

    const pageNodes = Array.from(doc.querySelectorAll('article')) as HTMLElement[];
    if (!pageNodes.length) return;

    pageNodes.forEach((page) => {
      const style = doc.defaultView?.getComputedStyle(page);
      const padTop = style ? parseFloat(style.paddingTop || '0') : 0;
      const padBottom = style ? parseFloat(style.paddingBottom || '0') : 0;
      const pageRect = page.getBoundingClientRect();
      const contentTop = pageRect.top + padTop;
      const contentBottom = pageRect.bottom - padBottom;

      const containers = Array.from(page.querySelectorAll('.wonguk_img_container')) as HTMLElement[];
      containers.forEach((container) => {
        // 좌측 컬럼의 하단 이미지 (일지)
        const leftCol = container.querySelector('.col.left') as HTMLElement | null;
        if (leftCol) {
          const leftImages = Array.from(leftCol.querySelectorAll('.img_wrapper')) as HTMLElement[];
          const leftBottomImg = leftImages[leftImages.length - 1]; // 마지막 이미지 (일지)

          if (leftBottomImg) {
            const imgRect = leftBottomImg.getBoundingClientRect();
            const fits = imgRect.top >= contentTop && imgRect.bottom <= contentBottom;
            const leftIndicator = leftCol.querySelector('.jasu_left_cut_indicator') as HTMLElement | null;

            if (!fits) {
              if (leftBottomImg.style.display !== 'none') leftBottomImg.style.display = 'none';
              if (!leftIndicator) {
                const ind = doc.createElement('div');
                ind.className = 'jasu_left_cut_indicator';
                ind.textContent = '⚠️ 일지 이미지가 페이지 여백을 넘어 생략되었습니다.';
                ind.setAttribute(
                    'style',
                    [
                      'margin-top:10px',
                      'width:100%',
                      'text-align:center',
                      'font-size:12px',
                      'font-weight:600',
                      'line-height:1.2',
                      'color:#7c2d12',
                      'background:rgba(254,243,199,0.9)',
                      'border:1px dashed #f59e0b',
                      'border-radius:4px',
                      'padding:6px 8px'
                    ].join(';')
                );
                leftCol.appendChild(ind);
              }
            } else {
              if (leftBottomImg.style.display === 'none') leftBottomImg.style.display = '';
              if (leftIndicator) leftIndicator.remove();
            }
          }
        }

        // 우측 컬럼의 하단 이미지 (월지)
        const rightCol = container.querySelector('.col.right') as HTMLElement | null;
        if (rightCol) {
          const rightImages = Array.from(rightCol.querySelectorAll('.img_wrapper')) as HTMLElement[];
          const rightBottomImg = rightImages[rightImages.length - 1]; // 마지막 이미지 (월지)

          if (rightBottomImg) {
            const imgRect = rightBottomImg.getBoundingClientRect();
            const fits = imgRect.top >= contentTop && imgRect.bottom <= contentBottom;
            const rightIndicator = rightCol.querySelector('.jasu_right_cut_indicator') as HTMLElement | null;

            if (!fits) {
              if (rightBottomImg.style.display !== 'none') rightBottomImg.style.display = 'none';
              if (!rightIndicator) {
                const ind = doc.createElement('div');
                ind.className = 'jasu_right_cut_indicator';
                ind.textContent = '⚠️ 월지 이미지가 페이지 여백을 넘어 생략되었습니다.';
                ind.setAttribute(
                    'style',
                    [
                      'margin-top:10px',
                      'width:100%',
                      'text-align:center',
                      'font-size:12px',
                      'font-weight:600',
                      'line-height:1.2',
                      'color:#7c2d12',
                      'background:rgba(254,243,199,0.9)',
                      'border:1px dashed #f59e0b',
                      'border-radius:4px',
                      'padding:6px 8px'
                    ].join(';')
                );
                rightCol.appendChild(ind);
              }
            } else {
              if (rightBottomImg.style.display === 'none') rightBottomImg.style.display = '';
              if (rightIndicator) rightIndicator.remove();
            }
          }
        }
      });
    });
  }, []);

  // 카테고리 검색 처리
  const handleCategorySearch = useCallback(async (value: string) => {
    if (value.trim().length > 0) {
      const result = await searchCategories(value);
      setCategoryList(result);
      setShowCategoryList(result.length > 0);
    } else {
      setCategoryList([]);
      setShowCategoryList(false);
    }
  }, [searchCategories]);

  // 카테고리 입력 처리 (디바운싱 적용)
  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCategoryName(value);

    // 이전 타이머 클리어
    if (categoryTimeoutRef.current) {
      clearTimeout(categoryTimeoutRef.current);
    }

    // 새로운 타이머 설정 (300ms 디바운싱)
    categoryTimeoutRef.current = setTimeout(() => {
      handleCategorySearch(value);
    }, 300);
  };

  // 카테고리 선택 처리
  const handleCategorySelect = (category: categories) => {
    setCategoryName(category.categoryName);
    setShowCategoryList(false);
    setCategoryList([]);
  };

  // 외부 클릭 시 목록 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-input-wrapper')) {
        setShowCategoryList(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // 가격 입력 처리
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      setPrice('');
    } else {
      setPrice(Number(value).toLocaleString());
    }
  };

  // 할인가격 입력 처리
  const handleDiscountPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '') {
      setDiscountPrice('');
    } else {
      setDiscountPrice(Number(value).toLocaleString());
    }
  };

  // 썸네일 파일 상태
  const [thumbnailFiles, setThumbnailFiles] = useState<File[]>([]);

  const [inlineFiles, setInlineFiles] = useState<File[]>([]);

  const [endFiles, setEndFiles] = useState<File[]>([]);

  // 상세 이미지 파일 상태
  const [detailFiles, setDetailFiles] = useState<File[]>([]);

  // A4 비율 검증 함수
  const validateA4Ratio = (width: number, height: number): boolean => {
      const ratio = height / width;
      const a4Ratio = 1.414; // A4 비율 (297/210)
      const tolerance = 0.05; // 5% 오차 허용
      return Math.abs(ratio - a4Ratio) <= tolerance;
  };

  // 썸네일 업로드 처리
  useEffect(() => {
    const uploadThumbnail = async () => {
      if (thumbnailFiles.length > 0) {
        const file = thumbnailFiles[0];

        // 이미지 치수 검증
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            // A4 비율 검증
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setThumbnailFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewThumbnail({ file, id: response.data.id });
              setThumbnailFiles([]); // 업로드 후 초기화
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };

          img.src = e.target?.result as string;
        };

        reader.readAsDataURL(file);
      }
    };

    uploadThumbnail();
  }, [thumbnailFiles, productDetail, productId]);

  // 속지 업로드 처리
  useEffect(() => {
    const uploadInline = async () => {
      if (inlineFiles.length > 0) {
        const file = inlineFiles[0];

        // 이미지 치수 검증
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            // A4 비율 검증
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setInlineFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewInline({ file, id: response.data.id });
              setInlineFiles([]); // 업로드 후 초기화
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };

          img.src = e.target?.result as string;
        };

        reader.readAsDataURL(file);
      }
    };

    uploadInline();
  }, [inlineFiles]);

  // 맺음 이미지 업로드 처리
  useEffect(() => {
    const uploadEnd = async () => {
      if (endFiles.length > 0) {
        const file = endFiles[0];

        // 이미지 치수 검증
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = (e) => {
          img.onload = async () => {
            // A4 비율 검증
            if (!validateA4Ratio(img.width, img.height)) {
              toast.error('이미지가 A4 비율(210:297)이 아닙니다. A4 비율의 이미지를 업로드해주세요.');
              setEndFiles([]);
              return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
              const response = await apiClient.post('/upload-file/upload', formData);
              setNewEndImage({ file, id: response.data.id });
              setEndFiles([]); // 업로드 후 초기화
            } catch (error) {
              console.error('이미지 업로드 실패:', error);
              toast.error('이미지 업로드를 실패했습니다.');
            }
          };

          img.src = e.target?.result as string;
        };

        reader.readAsDataURL(file);
      }
    };

    uploadEnd();
  }, [endFiles]);

  // 상세 이미지 업로드 처리
  useEffect(() => {
    const uploadDetailImages = async () => {
      const validatedFiles: File[] = [];

      // 모든 파일의 A4 비율 검증
      for (const file of detailFiles) {
        await new Promise((resolve) => {
          const img = new Image();
          const reader = new FileReader();

          reader.onloadend = (e) => {
            img.onload = () => {
              if (validateA4Ratio(img.width, img.height)) {
                validatedFiles.push(file);
              } else {
                toast.error(`${file.name}이(가) A4 비율(210:297)이 아닙니다. A4 비율의 이미지만 업로드됩니다.`);
              }
              resolve(true);
            };

            img.src = e.target?.result as string;
          };

          reader.readAsDataURL(file);
        });
      }

      // 검증된 파일들을 순서대로 업로드하고 결과를 배열에 저장
      const uploadedImages: {file: File; id: string}[] = [];

      for (const file of validatedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await apiClient.post('/upload-file/upload', formData);
          uploadedImages.push({ file, id: response.data.id });
        } catch (error) {
          console.error('이미지 업로드 실패:', error);
          toast.error('이미지 업로드를 실패했습니다.');
        }
      }

      // 업로드가 모두 완료된 후 한 번에 상태 업데이트
      if (uploadedImages.length > 0) {
        setNewDetailImages(prev => [...prev, ...uploadedImages]);
      }

      setDetailFiles([]); // 업로드 후 초기화
    };

    if (detailFiles.length > 0) {
      uploadDetailImages();
    }
  }, [detailFiles]);

  // 샘플파일 업로드 처리 (PDF)
  useEffect(() => {
    const uploadSampleFile = async () => {
      if (sampleFiles.length > 0) {
        const file = sampleFiles[0];

        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await apiClient.post('/upload-file/upload', formData);
          setNewSampleFile({ file, id: response.data.id });
          setSampleFiles([]); // 업로드 후 초기화
        } catch (error) {
          console.error('파일 업로드 실패:', error);
          toast.error('파일 업로드를 실패했습니다.');
        }
      }
    };

    uploadSampleFile();
  }, [sampleFiles]);

  // 수정 처리
  const handleSubmit = useCallback(async () => {
    if (!price) {
      toast.error('금액을 입력해주세요.');
      return;
    }
    if (!description) {
      toast.error('간단 설명을 입력해주세요.');
      return;
    }

    if (!productId) {
      return;
    }

    const updateData: UpdateProductRequest = {
      custInfoCount: parseInt(custInfoCount) as 1 | 2,
      price: parseInt(price.replaceAll(',', '')),
      description,
    };

    if (discountPrice) {
      updateData.discountPrice = parseInt(discountPrice.replaceAll(',', ''));
    } else {
      updateData.discountPrice = 0;
    }

    // 새로운 이미지가 업로드되었을 때만 추가
    if (newThumbnail) {
      updateData.thumbnailFileId = newThumbnail.id;
    }
    if(newInline) {
      updateData.inlineFileId = newInline.id;
    }
    if (newDetailImages.length > 0) {
      updateData.detailFileIds = newDetailImages.map(item => item.id);
    }

    if (newSampleFile) {
      updateData.sampleFileId = newSampleFile.id;
    }

    if (newEndImage) {
      updateData.endFileId = newEndImage.id;
    }

    // 카테고리 이름 추가
    if (categoryName) {
      updateData.categoryName = categoryName;
    }

    try {
      await updateProduct(productId, updateData);
      toast.success('상품이 성공적으로 수정되었습니다!');
      navigate(`/product/${productId}`);
    } catch (error) {
      console.error('상품 수정 실패:', error);
      toast.error('상품 수정에 실패했습니다.');
    }
  }, [productId, price, discountPrice, description, custInfoCount, newThumbnail, newDetailImages, updateProduct, navigate, newInline, categoryName, newSampleFile, newEndImage]);

  const closeTemplateModal = async () => {
    await updatePreview({ force: true });
    setTemplateModalOpen(false);
  };

  const closeTemplateFontModal = async () => {
    await updatePreview({ force: true });
    setTemplateFontModalOpen(false);
  };

  return <>
    {/* 헤더 */}
    <div className="!flex !items-center !gap-3 !mb-4 !pt-4">
      <button
        className="!flex !items-center !justify-center !w-8 !h-8 !rounded-full !bg-white !shadow-sm hover:!bg-gray-50 !transition-colors !border-0"
        onClick={() => navigate('/product')}
        type="button"
      >
        <svg className="!w-4 !h-4 !text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <h1 className="!text-xl !font-bold !text-gray-900 !m-0">상품 수정</h1>
    </div>

    <div className="!grid !grid-cols-1 lg:!grid-cols-12 !gap-6">
      {/* 왼쪽: 정보 입력 */}
      <div className="lg:!col-span-8 !space-y-4">
        {/* 기본 정보와 가격 정보를 2단 그리드로 배치 */}
        <div className="!grid !grid-cols-1 md:!grid-cols-2 !gap-4">
          {/* 기본 정보 섹션 */}
          <div className="!bg-white !rounded-lg !p-5">
            <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">기본 정보</h2>

            <div className="!space-y-4">
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">상품 코드</label>
                <Input
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                  textAlign="left"
                  placeholder="예: SAJU-001"
                  style={{ width: '100%' }}
                  disabled={true}
                />
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">서비스 형태 *</label>
                <select
                  value={custInfoCount}
                  onChange={(e) => setCustInfoCount(e.target.value)}
                  className="!w-full !h-[42px] !px-3 !border !border-gray-300 !rounded-lg !text-sm !bg-white !cursor-pointer !transition-colors !duration-200 focus:!outline-none focus:!border-blue-400"
                >
                  <option value="">서비스 형태를 선택하세요</option>
                  <option value="1">1인 서비스</option>
                  <option value="2">2인 서비스</option>
                </select>
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">상품명</label>
                <Input
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  textAlign="left"
                  placeholder="상품명을 입력하세요"
                  style={{ width: '100%' }}
                  disabled={true}
                />
              </div>
              <div className="!space-y-1 category-input-wrapper">
                <label className="!block !text-xs !font-medium !text-gray-700">카테고리 명</label>
                <div className="!relative">
                  <Input
                      value={categoryName}
                      onChange={handleCategoryChange}
                      textAlign="left"
                      placeholder="카테고리를 입력하세요"
                      style={{ width: '100%' }}
                  />
                  {showCategoryList && categoryList.length > 0 && <div className="!absolute !z-10 !w-full !mt-1 !bg-white !border !border-gray-300 !rounded-lg !shadow-lg !max-h-48 !overflow-y-auto">
                      {categoryList.map((category) => (
                        <div
                          key={category.id}
                          className="!px-3 !py-2 !cursor-pointer hover:!bg-gray-100 !text-sm !transition-colors"
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category.categoryName}
                        </div>
                      ))}
                    </div>}
                </div>
              </div>
              <div className="!space-y-1">
                <label className="!block !text-xs !font-medium !text-gray-700">상품 샘플 (PDF)</label>
                {existingSampleFile && !newSampleFile ? <div className="!flex !items-center !gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPreviewPdfUrl(existingSampleFile);
                        setShowPreviewModal(true);
                      }}
                      className="!flex-1 !px-3 !py-2 !bg-gray-100 !rounded-lg !text-sm !text-blue-600 hover:!bg-gray-200 !transition-colors"
                    >
                      기존 샘플 파일 보기
                    </button>
                    <button
                      type="button"
                      onClick={() => setExistingSampleFile('')}
                      className="!px-3 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors"
                    >
                      변경
                    </button>
                  </div> : newSampleFile ? <div className="!flex !items-center !gap-2">
                    <div className="!flex-1 !px-3 !py-2 !bg-green-50 !rounded-lg !text-sm !text-green-700">
                      {newSampleFile.file.name}
                    </div>
                    <button
                      type="button"
                      onClick={() => setNewSampleFile(null)}
                      className="!px-3 !py-2 !bg-red-500 !text-white !text-sm !rounded-lg hover:!bg-red-600 !transition-colors"
                    >
                      삭제
                    </button>
                  </div> : <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setSampleFiles(Array.from(e.target.files));
                      }
                    }}
                    className="!w-full !px-3 !py-2 !border !border-gray-300 !rounded-lg !text-sm !bg-white !cursor-pointer file:!mr-3 file:!py-1 file:!px-3 file:!rounded file:!border-0 file:!text-sm file:!font-medium file:!bg-blue-50 file:!text-blue-700 hover:file:!bg-blue-100"
                  />}
              </div>
            </div>
          </div>

          {/* 가격 정보 섹션 */}
          <div className="!bg-white !rounded-lg !p-5">
            <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">가격 정보</h2>

            <div className="!space-y-4">
              <div className="!space-y-1">
                <span style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>기본 가격</span>
                <Input
                  value={price}
                  onChange={handlePriceChange}
                  textAlign="left"
                  placeholder="기본 가격 입력"
                  suffix="₩"
                  style={{ width: '100%' }}
                />
              </div>
              <div className="!space-y-1">
                <span style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>할인 가격</span>
                <Input
                  value={discountPrice}
                  onChange={handleDiscountPriceChange}
                  textAlign="left"
                  placeholder="할인 적용가 입력"
                  suffix="₩"
                  style={{ width: '100%' }}
                />
                {discountPrice && price && <span style={{ fontSize: '12px', color: '#ff6b6b', marginTop: '4px', display: 'block' }}>
                    할인율: {getDiscountRate(discountPrice, price)}%
                  </span>}
              </div>
            </div>
          </div>
        </div>

        {/* 상품 설명 섹션 - 전체 너비 사용 */}
        <div className="!bg-white !rounded-lg !p-5">
          <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">상품 설명</h2>
          <div className="!space-y-1">
            <label className="!block !text-xs !font-medium !text-gray-700">상품 설명 *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="상품에 대한 간단한 설명을 입력하세요"
              className="!w-full !min-h-[80px] !px-3 !py-2.5 !border !border-gray-300 !rounded-lg !text-sm !resize-y !font-sans !transition-colors !duration-200 focus:!outline-none focus:!border-blue-400 placeholder:!text-gray-400"
            />
          </div>
        </div>

        {/* 이미지 섹션 */}
        <div className="!bg-white !rounded-lg !p-5">
          <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">상품 이미지</h2>

          <div className="!grid !grid-cols-1 md:!grid-cols-3 !gap-4">
            <div className="!space-y-1">
              <label className="!block !text-xs !font-medium !text-gray-700">대표 이미지</label>
              {existingThumbnail && !newThumbnail ? <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                        src={existingThumbnail}
                        alt="thumbnail"
                        className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <div className="!absolute !top-2 !right-2">
                    <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                      기존 이미지
                    </div>
                  </div>
                  <button
                    className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                    onClick={() => setExistingThumbnail('')}
                    type="button"
                  >
                    변경
                  </button>
                </div> : newThumbnail ? <div className="!relative">
                  <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                    <img
                        src={URL.createObjectURL(newThumbnail.file)}
                        alt="thumbnail"
                        className="!w-full !h-full !object-contain"
                    />
                  </div>
                  <button
                    className="!absolute !top-2 !right-2 !px-5 !py-3 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                    onClick={() => setNewThumbnail(null)}
                    type="button"
                  >
                    삭제
                  </button>
                </div> : <div className="!aspect-square !border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors">
                  <FileUpload setFiles={setThumbnailFiles} accept="image/*" multiple={false} />
                </div>}
            </div>

            {/* 속지 이미지*/}
            <div className="!space-y-1">
              <label className="!block !text-xs !font-medium !text-gray-700">속지 이미지</label>
              {existingInline && !newInline ? <div className="!relative">
                    <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                      <img
                          src={existingInline}
                          alt="inline"
                          className="!w-full !h-full !object-contain"
                      />
                    </div>
                    <div className="!absolute !top-2 !right-2">
                      <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                        기존 이미지
                      </div>
                    </div>
                    <button
                        className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                        onClick={() => setExistingInline('')}
                        type="button"
                    >
                      변경
                    </button>
                  </div> : newInline ? <div className="!relative">
                    <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                      <img
                          src={URL.createObjectURL(newInline.file)}
                          alt="inline"
                          className="!w-full !h-full !object-contain"
                      />
                    </div>
                    <button
                        className="!absolute !top-2 !right-2 !px-5 !py-3 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                        onClick={() => setNewInline(null)}
                        type="button"
                    >
                      삭제
                    </button>
                  </div> : <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                    <FileUpload setFiles={setInlineFiles} accept="image/*" multiple={false} />
                  </div>}
            </div>

            {/* 맺음말 이미지*/}
            <div className="!space-y-1">
              <label className="!block !text-xs !font-medium !text-gray-700">맺음말 이미지</label>
              {existingEnd && !newEndImage ? <div className="!relative">
                    <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                      <img
                          src={existingEnd}
                          alt="inline"
                          className="!w-full !h-full !object-contain"
                      />
                    </div>
                    <div className="!absolute !top-2 !right-2">
                      <div className="!px-2 !py-1 !bg-gray-500 !text-white !text-xl !rounded">
                        기존 이미지
                      </div>
                    </div>
                    <button
                        className="!absolute !bottom-2 !right-2 !px-5 !py-3 !bg-blue-500 !text-white !text-xl !rounded hover:!bg-blue-600 !transition-colors !border-0"
                        onClick={() => setExistingEnd('')}
                        type="button"
                    >
                      변경
                    </button>
                  </div> : newEndImage ? <div className="!relative">
                    <div className="!rounded-lg !overflow-hidden !border-2 !border-gray-200 !bg-gray-50" style={{aspectRatio: '210/297'}}>
                      <img
                          src={URL.createObjectURL(newEndImage.file)}
                          alt="endImage"
                          className="!w-full !h-full !object-contain"
                      />
                    </div>
                    <button
                        className="!absolute !top-2 !right-2 !px-5 !py-3 !bg-red-500 !text-white !text-xl !rounded hover:!bg-red-600 !transition-colors !border-0"
                        onClick={() => setNewEndImage(null)}
                        type="button"
                    >
                      삭제
                    </button>
                  </div> : <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !flex !items-center !justify-center hover:!border-gray-400 !transition-colors" style={{aspectRatio: '210/297'}}>
                    <FileUpload setFiles={setEndFiles} accept="image/*" multiple={false} />
                  </div>}
            </div>

            <div className="!space-y-1">
              <label className="!block !text-xs !font-medium !text-gray-700">상세 이미지</label>
              <div className="!space-y-2">
                {/* 기존 이미지가 있고 새 이미지가 없을 때만 표시 */}
                {existingDetailImages.length > 0 && newDetailImages.length === 0 && <>
                    <div className="!text-xs !text-gray-600 !mb-2">
                      기존 이미지 ({existingDetailImages.length}개) - 새 이미지를 업로드하면 전체가 교체됩니다
                    </div>
                    <div className="!grid !grid-cols-3 !gap-2">
                      {existingDetailImages.map((url, index) => (
                        <div key={`existing-${index}`} className="!relative !group">
                          <div className="!aspect-square !rounded-lg !overflow-hidden !border-2 !border-gray-200">
                            <img
                              src={url}
                              alt={`existing-${index}`}
                              className="!w-full !h-full !object-cover"
                            />
                          </div>
                          <div className="!absolute !top-1 !right-1">
                            <div className="!px-1 !py-0.5 !bg-gray-500 !text-white !text-m !rounded">
                              기존
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // 기존 이미지를 숨기고 새 이미지 업로드 UI 표시
                        setExistingDetailImages([]);
                      }}
                      className="!w-full !px-3 !py-2 !bg-red-500 !text-white !text-sm !rounded hover:!bg-red-600 !transition-colors"
                    >
                      전체 이미지 교체하기
                    </button>
                  </>}

                {/* 새로운 이미지 표시 */}
                {newDetailImages.length > 0 && <>
                    <div className="!flex !justify-between !items-center !mb-2">
                      <div className="!text-xs !text-green-600">
                        새로운 이미지 ({newDetailImages.length}개) - 저장 시 기존 이미지를 모두 교체합니다
                      </div>
                      {productDetail?.detailFiles && productDetail.detailFiles.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setNewDetailImages([]);
                            setExistingDetailImages(productDetail.detailFiles.map(file => file.fileFullUrl));
                          }}
                          className="!text-xs !text-blue-600 hover:!text-blue-800 !underline"
                        >
                          기존 이미지로 되돌리기
                        </button>
                      )}
                    </div>
                    <div className="!grid !grid-cols-3 !gap-2">
                    {newDetailImages.map((file, index) => (
                      <div key={index} className="!relative !group">
                        <div className="!aspect-square !rounded-lg !overflow-hidden !border-2 !border-gray-200">
                          <img
                            src={URL.createObjectURL(file.file)}
                            alt={`preview-${index}`}
                            className="!w-full !h-full !object-cover"
                          />
                        </div>
                        <button
                          className="!absolute !top-1 !right-1 !px-1 !py-0.5 !bg-red-500 !text-white !text-xs !rounded hover:!bg-red-600 !transition-colors !border-0"
                          onClick={() => setNewDetailImages(prev => prev.filter((_, i) => i !== index))}
                          type="button"
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                    </div>
                  </>}

                {/* 업로드 버튼 */}
                <div className="!aspect-square !border-2 !border-dashed !border-gray-300 !rounded-lg hover:!border-gray-400 hover:!bg-gray-50 !transition-all !cursor-pointer">
                  <FileUpload setFiles={setDetailFiles} accept="image/*" multiple={true} />
                </div>
              </div>
            </div>
          </div>
          {/* 템플릿 CSS 이미지 관리 섹션 */}
          <div className="!bg-white !rounded-lg !p-5">
            <h2 className="!text-base !font-semibold !text-gray-900 !mb-4">템플릿 디자인 요소</h2>

            <div className="!space-y-3">
              <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                <div>
                  <h3 className="!text-sm !font-medium !text-gray-900">스타일 이미지</h3>
                  <p className="!text-xs !text-gray-500 !mt-1">상품에 적용되는 변경 가능한 상세 이미지 신규 등록</p>
                </div>
                <button
                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                    onClick={() => {
                      setTemplateModalOpen(true);
                    }}
                    type="button"
                >
                  신규 스타일 등록
                </button>
              </div>
            </div>
            <div className="!space-y-3 !mt-3">
              <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                <div>
                  <h3 className="!text-sm !font-medium !text-gray-900">폰트</h3>
                  <p className="!text-xs !text-gray-500 !mt-1">상품에 적용 되는 변경가능한 폰트 등록</p>
                </div>
                <button
                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                    onClick={() => {
                      setTemplateFontModalOpen(true);
                    }}
                    type="button"
                >
                  신규 폰트 등록
                </button>
              </div>
            </div>
            <div className="!space-y-3 !mt-3">
              <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                <div>
                  <h3 className="!text-sm !font-medium !text-gray-900">스타일 항목 변경</h3>
                  <p className="!text-xs !text-gray-500 !mt-1">원국표 등 상세 스타일 변경</p>
                </div>
                <button
                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                    onClick={() => {
                      setTemplateStyleModalOpen(true);
                    }}
                    type="button"
                >
                  스타일 항목 변경
                </button>
              </div>
            </div>
            <div className="!space-y-3 !mt-3">
              <div className="!flex !items-center !justify-between !p-3 !bg-gray-50 !rounded-lg">
                <div>
                  <h3 className="!text-sm !font-medium !text-gray-900">기본 이미지 변경</h3>
                  <p className="!text-xs !text-gray-500 !mt-1">기본 대표, 속지, 맺음말 이미지 변경</p>
                </div>
                <button
                    className="!px-4 !py-2 !bg-blue-500 !text-white !text-sm !rounded-lg hover:!bg-blue-600 !transition-colors !border-0"
                    onClick={() => {
                      setDefaultImageModalOpen(true);
                    }}
                    type="button"
                >
                  기본 이미지 변경
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 오른쪽: 미리보기 */}
      <div className="lg:!col-span-4">
        <div className="!sticky !top-0">
          <div className="!bg-white !rounded-lg !p-5">
            <h3 className="!text-base !font-semibold !text-gray-900 !mb-4">디자인 미리보기</h3>

            {/* 미리보기 영역 - 점선 테두리로 구분 */}
            <div className="!border-2 !border-dashed !border-gray-300 !rounded-lg !bg-white !overflow-hidden"
                 style={{
                   width: '340px',
                   height: '481px'
                 }}>
              <iframe
                  ref={(iframe) => {
                    iframeRef.current = iframe;
                    if (iframe && previewHtml) {
                      // 스크롤 위치 저장 이벤트 등록
                      const saveScrollPosition = () => {
                        const iframeWin = iframe.contentWindow;
                        if (iframeWin) {
                          scrollPositionRef.current = {
                            x: iframeWin.scrollX || 0,
                            y: iframeWin.scrollY || 0
                          };
                        }
                      };

                      iframe.onload = () => {
                        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                        const iframeWin = iframe.contentWindow;

                        if (iframeDoc && iframeWin) {
                          // HTML에 이미 포함된 스타일을 그대로 사용하도록 함
                          // wonguk 컨텐츠가 여백을 넘으면 bottom row 제거
                          try {
                            enforceWongukBounds(iframe);
                            // 이미지 로딩 후 레이아웃 변동을 고려해 한 번 더 체크
                            const imgs = Array.from(iframeDoc.images || []);
                            let pending = imgs.length;
                            if (pending === 0) {
                              enforceWongukBounds(iframe);
                            } else {
                              imgs.forEach((img) => {
                                if (img.complete) {
                                  pending -= 1;
                                  if (pending === 0) {
                                    enforceWongukBounds(iframe);
                                    enforceJasuBounds(iframe);
                                  }
                                } else {
                                  img.addEventListener('load', () => {
                                    pending -= 1;
                                    if (pending === 0) {
                                      enforceWongukBounds(iframe);
                                      enforceJasuBounds(iframe);
                                    }
                                  }, { once: true });
                                  img.addEventListener('error', () => {
                                    pending -= 1;
                                    if (pending === 0) {
                                      enforceWongukBounds(iframe);
                                      enforceJasuBounds(iframe);
                                    }
                                  }, { once: true });
                                }
                              });
                            }
                          } catch {}

                          // 스크롤 위치 복원
                          if (scrollPositionRef.current.y > 0 || scrollPositionRef.current.x > 0) {
                            setTimeout(() => {
                              iframeWin.scrollTo(scrollPositionRef.current.x, scrollPositionRef.current.y);
                            }, 100);
                          }

                          // 스크롤 이벤트 리스너 등록
                          iframeWin.addEventListener('scroll', saveScrollPosition);
                        }
                      };
                    }
                  }}
                  style={{
                    width: '210mm', // A4 폭
                    height: '297mm', // A4 높이
                    transform: 'scale(0.42)', // 340px / (210mm * 3.78) ≈ 0.43
                    transformOrigin: 'top left',
                    border: 'none',
                    display: 'block'
                  }}
                  srcDoc={previewHtml}
                  title="상품 미리보기"
              />
            </div>
          </div>

          {/* 수정하기 버튼 */}
          <div className="!mt-4">
            <BigButton type="submit" text="상품 수정하기" onClick={handleSubmit} />
          </div>
        </div>
      </div>
    </div>

    {/* 미리보기 모달 */}
    {showPreviewModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg flex flex-col" style={{ width: '850px', height: '85vh' }}>
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">샘플 파일 미리보기</h3>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            <iframe
                src={previewPdfUrl}
                className="w-full h-full border-0"
                title="PDF 미리보기"
            />
          </div>
        </div>
      </div>}

    {/* 템플릿 이미지 모달 */}
    <TemplateImageModal
        isOpen={templateModalOpen}
        onClose={() => closeTemplateModal()}
        title={'스타일 이미지 선택'}
        productId={productId}/>
    {/* 템플릿 폰트 모달 */}
    <TemplateFontModal
        isOpen={templateFontModalOpen}
        onClose={() => closeTemplateFontModal()}
        title={'폰트 변경'}
        productId={productId}/>
    {/* 기본 이미지 변경 모달 */}
    <DefaultImageModal
        isOpen={defaultImageModalOpen}
        onClose={() => {
          setDefaultImageModalOpen(false)
          getProductDetail(productId);
          updatePreview()
        }}
        title={'기본 이미지 변경'}
        productId={productId}/>
    <TemplateStyleModal
        isOpen={templateStyleModalOpen}
        onClose={() => closeTemplateStyleModal()}
        title={'스타일 변경'}
        productId={productId}
    />
  </>;
};

export default ProductUpdate;
